import { useState, useEffect, useCallback, type RefObject, type CSSProperties } from "react"

/**
 * useMenuPosition — clamp a fixed-position menu to viewport edges.
 *
 * Measures the rendered menu element and adjusts left/top so it never
 * overflows the viewport.
 *
 * The measurement is repeated whenever the element resizes. A single pass on
 * the first animation frame is not enough: menus whose content arrives
 * asynchronously (e.g. the workflow status list) are still an almost-empty
 * shell at first paint, so the clamp concludes they fit and never revisits
 * that decision once the content grows. A ResizeObserver keeps the clamp
 * honest for the element's real height.
 *
 * @param position  Raw { x, y } from the pointer / touch event.
 * @param ref       Ref to the menu's root HTML element.
 * @param pad       Minimum distance from viewport edges (default 12px).
 */
/** Extra clearance below the menu, on top of the bottom safe-area inset. */
const BOTTOM_GAP = 16

/** Never shrink a menu below this, however cramped the viewport is. */
const MIN_MENU_HEIGHT = 160

export function useMenuPosition(
  position: { x: number; y: number },
  ref: RefObject<HTMLDivElement | null>,
  pad = 12,
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({
    left: position.x,
    top: position.y,
    visibility: "hidden" as const,
  })

  const clamp = useCallback(() => {
    const el = ref.current
    if (!el) {
      setStyle({ left: position.x, top: position.y })
      return
    }
    const rect = el.getBoundingClientRect()
    let x = position.x
    let y = position.y

    // Read safe area insets (iOS notch/status bar)
    const styles = getComputedStyle(document.documentElement)
    const safeTop = parseInt(styles.getPropertyValue("--status-bar-height") || "0", 10) || pad
    const safeBottom = parseInt(styles.getPropertyValue("--safe-area-bottom") || "0", 10) || pad

    // Clamp horizontally. minX/maxX first, then pick whichever bound is
    // more restrictive so a too-wide menu still ends up on-screen (biased
    // to the left edge) instead of overflowing the right edge again.
    const minX = pad
    const maxX = Math.max(minX, window.innerWidth - rect.width - pad)
    x = Math.min(Math.max(x, minX), maxX)

    // Clamp vertically (respect safe area top/bottom) the same way. Doing
    // this as a single min/max — rather than two independent `if`s — keeps
    // the container's bottom edge from ending up past the viewport after
    // the top clamp has already moved it: with two independent checks, a
    // menu taller than the available space could get pinned to the top
    // safe-area edge by the second check without re-verifying that its
    // (already-computed) bottom-aligned position still fits.
    const minY = Math.max(safeTop, pad)
    const maxY = Math.max(minY, window.innerHeight - rect.height - Math.max(safeBottom, pad))
    y = Math.min(Math.max(y, minY), maxY)

    // Cap the height to what is actually usable, and publish it as maxHeight.
    //
    // The menus used to cap themselves with `max-h-[calc(100dvh-24px)]`, which
    // ignores the status bar and the bottom safe area. Measured on Android with
    // the keyboard open: innerHeight 578, so that rule allowed 554px while only
    // 578 - 50 (status bar) - 24 (safe area) = 504px was reachable. The clamp
    // above then pinned the panel at minY and it overflowed the bottom edge by
    // 26px, putting the last row ("Make Heading") off-screen.
    //
    // BOTTOM_GAP is on top of the safe-area inset so the menu never sits flush
    // against the keyboard — with KeyboardResize.Native the WebView shrinks, so
    // innerHeight already ends where the keyboard begins and a bare inset reads
    // as "glued to the keyboard".
    const usableHeight = Math.max(
      MIN_MENU_HEIGHT,
      window.innerHeight - Math.max(safeTop, pad) - Math.max(safeBottom, pad) - BOTTOM_GAP,
    )

    setStyle((prev) =>
      prev.left === x && prev.top === y && prev.maxHeight === usableHeight
        ? prev
        : { left: x, top: y, maxHeight: usableHeight },
    )
  }, [position.x, position.y, ref, pad])

  useEffect(() => {
    const raf = requestAnimationFrame(clamp)

    // Re-clamp while the menu grows into its final size, and on viewport
    // changes (rotation, keyboard, window resize).
    const el = ref.current
    const ro = el ? new ResizeObserver(clamp) : null
    if (el && ro) ro.observe(el)
    window.addEventListener("resize", clamp)
    window.addEventListener("orientationchange", clamp)

    return () => {
      cancelAnimationFrame(raf)
      ro?.disconnect()
      window.removeEventListener("resize", clamp)
      window.removeEventListener("orientationchange", clamp)
    }
  }, [clamp, ref])

  return style
}
